import React, { useRef } from 'react';

/**
 * Tilt3D — wraps content with a cursor-driven 3D perspective tilt.
 * Sets CSS vars (--rx, --ry, --tz) on the wrapper so any element using
 * `.tilt-3d` styles (or this wrapper itself) gets the effect.
 *
 * Auto-disables on touch devices (uses `pointer: fine` query implicitly
 * because touchstart triggers a snap back).
 *
 * Props:
 *   max      Maximum tilt angle in degrees (default 8)
 *   lift     Z translate on hover in px (default 24)
 *   scale    Hover scale (default 1.0 — disabled, keep tilt-only)
 *   className extra classes
 */
export default function Tilt3D({
  children,
  max = 8,
  lift = 24,
  className = '',
  ...rest
}) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;   // 0..1
    const y = (e.clientY - r.top) / r.height;   // 0..1
    const ry = (x - 0.5) * 2 * max;             // -max..max
    const rx = -((y - 0.5) * 2 * max);
    el.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
    el.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
    el.style.setProperty('--tz', `${lift}px`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', `0deg`);
    el.style.setProperty('--ry', `0deg`);
    el.style.setProperty('--tz', `0px`);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`tilt-3d ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
